import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useKnowledgeBases } from "./hooks/use-knowledge-bases";
import { useDeleteKnowledgeBase } from "./hooks/use-delete-knowledge-base";
import { Pagination } from "@/components/ui/pagination";
import { CreateKbDialog } from "./components/create-kb-dialog";
import { DeleteKbDialog } from "./components/delete-kb-dialog";
import type { KnowledgeBase } from "../domain/entities/knowledge-base";

const PAGE_SIZE = 10;

export const KnowledgeBasesPage = () => {
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<KnowledgeBase | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { data, isLoading } = useKnowledgeBases({
    page,
    limit: PAGE_SIZE,
    name: debouncedSearch || undefined,
  });

  const { mutate: deleteKb, isPending: isDeleting } = useDeleteKnowledgeBase();

  const handleDelete = (id: string) => {
    deleteKb(id, { onSuccess: () => setDeleteTarget(null) });
  };

  const items = data?.items ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="flex flex-col gap-6 p-8">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold tracking-tight">
          Knowledge Base
        </h1>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" />
          Create new Knowledge Base
        </Button>
      </div>

      <Input
        placeholder="Search by name…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        className="max-w-sm"
      />

      <div className="flex flex-col gap-5 ">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
            No knowledge bases found.
          </div>
        ) : (
          items.map((kb) => (
            <div
              key={kb.id}
              className="group flex cursor-pointer items-center gap-4 rounded-md px-4 py-3 transition-colors hover:bg-secondary rounded-lg border"
              onClick={() => navigate(`/knowledge-bases/${kb.id}`)}
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{kb.name}</p>
              </div>
              <Button
                variant="ghost"
                size="icon"
                className="size-8 shrink-0 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100"
                onClick={(e) => {
                  e.stopPropagation();
                  setDeleteTarget(kb);
                }}
                aria-label={`Delete ${kb.name}`}
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))
        )}
      </div>

      {total > PAGE_SIZE && (
        <div className="flex justify-end">
          <Pagination
            page={page}
            total={total}
            limit={PAGE_SIZE}
            onPageChange={setPage}
          />
        </div>
      )}

      <CreateKbDialog open={createOpen} onClose={() => setCreateOpen(false)} />

      <DeleteKbDialog
        target={deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        isPending={isDeleting}
      />
    </div>
  );
};
